import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { GripVertical, Plus } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { WorkspaceBadge } from '../../WorkspaceBadge';
import { IconButton, OverflowMenu, type OverflowMenuAction } from '../../ui';
import { useWorkspaceDrag, type WorkspaceDragModel } from '../hooks/use-workspace-drag';
import { useWorkspaceRows, type WorkspaceRowModel, type WorkspaceRowsModel } from '../hooks/use-workspace-rows';
import { RailTooltip } from './RailTooltip';
// `dragCss`, not `drag`: the css-module guard scans for `<alias>.<class>`.
import dragCss from '../workspace-drag.module.css';
import styles from './RailSpaces.module.css';

/** Figma `Sidebar-Menu` lists five Spaces, then "More spaces +N". */
export const RAIL_SPACES_SHOWN = 5;

/** A new key, so it carries no `orion-web:` prefix (see CLAUDE.md, "Why Orion still appears"). */
const LAST_SPACE_KEY = 'symphony-sidebar-space';

function readLastSpace(): string | null {
  try {
    return localStorage.getItem(LAST_SPACE_KEY);
  } catch {
    return null;
  }
}

// The closed strip shows ONE Space: the one the current page is in, else the last one opened in this
// browser, else the first.
function useShownWorkspace(rows: WorkspaceRowModel[]): WorkspaceRowModel | undefined {
  const active = rows.find((r) => r.active);
  const activeSlug = active?.ws.slug;
  const [last, setLast] = useState<string | null>(null);
  useEffect(() => setLast(readLastSpace()), []);
  useEffect(() => {
    if (!activeSlug) return;
    setLast(activeSlug);
    try {
      localStorage.setItem(LAST_SPACE_KEY, activeSlug);
    } catch {
      /* storage unavailable — the strip falls back to the first Space */
    }
  }, [activeSlug]);
  return active ?? rows.find((r) => r.ws.slug === last) ?? rows[0];
}

// The rail's Spaces (Figma `Sidebar-Menu` 22535:282583, hover 22603:315360). Open: the first five Spaces,
// the create "+" on the first row, then "More spaces +N", which unfolds the rest in place. A row's drag
// handle appears on hover, the same reordering the old rail and the mobile drawer have (useWorkspaceDrag).
// Closed: the shown Space's badge, opening a menu of every Space (Figma 22012:417341).
//
// Which Spaces there are, where each goes and what a click reports lives in useWorkspaceRows.
export function RailSpaces({ collapsed = false }: { collapsed?: boolean }) {
  const { t } = useTranslation();
  const { ready, rows, create } = useWorkspaceRows();
  const [expanded, setExpanded] = useState(false);
  // Folded, only the first five rows are on screen, so the drag must not swap a row into the sixth.
  const dnd = useWorkspaceDrag(rows, expanded ? Infinity : RAIL_SPACES_SHOWN);
  const shown = useShownWorkspace(rows);
  const { logPageClickBi } = useGenericBi();

  if (collapsed) return ready && shown ? <CollapsedSpaces rows={rows} shown={shown} create={create} /> : null;

  // While loading, hold one row's height; "Create a space" would flash before the Spaces arrive.
  if (!ready) return <div className={styles.placeholder} />;

  if (rows.length === 0) {
    return (
      <Link href={create.route} as={create.path} onClick={create.onClick} className={`orion-nav-item ${styles.createRow}`}>
        <Plus size={16} aria-hidden />
        <span className={styles.name}>{create.label}</span>
      </Link>
    );
  }

  const hidden = dnd.rows.length - RAIL_SPACES_SHOWN;
  const visible = expanded || hidden <= 0 ? dnd.rows : dnd.rows.slice(0, RAIL_SPACES_SHOWN);
  return (
    <div className={styles.list}>
      {visible.map((row, i) => (
        <SpaceRow
          key={row.ws.slug}
          row={row}
          dnd={dnd}
          trailing={
            i === 0 ? (
              <RailTooltip label={create.label} placement="below">
                <IconButton size="tiny" href={create.route} linkAs={create.path} onClick={create.onClick} aria-label={create.label} className={styles.quiet}>
                  <Plus size={16} aria-hidden />
                </IconButton>
              </RailTooltip>
            ) : null
          }
        />
      ))}
      {hidden > 0 && (
        <button type="button" className={`orion-nav-item ${styles.more}`} aria-expanded={expanded} onClick={() => {
            logPageClickBi({ page_name: 'menu', button_name: expanded ? 'less_spaces' : 'more_spaces', origin: 'menu' });
            setExpanded((open) => !open);
          }}>
          <span className={styles.name}>{expanded ? t('sidebar.showLess') : t('sidebar.moreSpaces')}</span>
          {!expanded && <span className={styles.count}>+{hidden}</span>}
        </button>
      )}
    </div>
  );
}

function SpaceRow({ row, dnd, trailing }: { row: WorkspaceRowModel; dnd: WorkspaceDragModel; trailing: React.ReactNode }) {
  const { t } = useTranslation();
  const { ws, active } = row;
  return (
    // The drag marker goes on the ROW, which holds the grip: the drag finds its row with `closest()` from the grip.
    <div
      {...dnd.rowProps()}
      className={`${styles.spaceRow} ${dragCss.row} ${dnd.isDragging(ws.id) ? dragCss.lifted : ''}`}
      style={dnd.liftStyle(ws.id)}
      data-current={active ? 'true' : undefined}
      data-dragging={dnd.isDragging(ws.id) ? 'true' : undefined}
    >
      <Link
        href={row.href}
        as={row.as}
        onClick={row.onClick}
        draggable={false}
        aria-current={active ? 'page' : undefined}
        title={ws.name}
        className={styles.link}
      >
        <WorkspaceBadge ws={ws} size={20} />
        <span className={styles.name}>{ws.name}</span>
      </Link>
      {dnd.canReorder && (
        <span className={`${dragCss.grip} ${styles.grip}`} role="button" tabIndex={0} aria-label={t('workspace.reorder')} {...dnd.gripProps(ws.id)}>
          <GripVertical size={16} aria-hidden />
        </span>
      )}
      {trailing}
    </div>
  );
}

function CollapsedSpaces({ rows, shown, create }: { rows: WorkspaceRowModel[]; shown: WorkspaceRowModel; create: WorkspaceRowsModel['create'] }) {
  const { t } = useTranslation();
  const router = useRouter();
  const actions: OverflowMenuAction[] = [
    ...rows.map((row) => ({
      key: row.ws.slug,
      label: row.ws.name,
      icon: <WorkspaceBadge ws={row.ws} size={16} />,
      onSelect: () => {
        row.onClick();
        void router.push(row.href, row.as);
      },
    })),
    {
      key: '__create',
      label: t('sidebar.newSpace'),
      icon: <Plus size={16} aria-hidden />,
      onSelect: () => {
        create.onClick();
        void router.push(create.route, create.path);
      },
    },
  ];
  return (
    <RailTooltip label={shown.ws.name}>
      <OverflowMenu
        ariaLabel={t('sidebar.workspaces')}
        actions={actions}
        selectedKey={shown.ws.slug}
        triggerIcon={<WorkspaceBadge ws={shown.ws} size={20} />}
        triggerSize="medium"
        align="start"
        menuClassName={styles.menu}
        triggerClassName={styles.badgeTrigger}
      />
    </RailTooltip>
  );
}
